import { useState, useCallback } from "react";
import { createRenewalOrder, verifyRenewalPayment } from "../apis/payment";

const useRenewalPayment = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const processRenewalPayment = useCallback(
    async (planType, instanceId, userInfo = {}) => {
      setIsProcessing(true);
      setError(null);

      try {
        // Call the createRenewalOrder API
        const orderResult = await createRenewalOrder(planType, instanceId);

        if (!orderResult.success) {
          throw new Error(
            orderResult.message || "Failed to create renewal order"
          );
        }

        // For paid plans - ensure Razorpay SDK is loaded
        if (!window.Razorpay) {
          throw new Error("Razorpay SDK not loaded");
        }

        // Set up Razorpay options for payment
        return new Promise((resolve, reject) => {
          const options = {
            key: orderResult.data.key,
            amount: orderResult.data.amount,
            currency: orderResult.data.currency,
            name: "Ping By BluTec.ai",
            description: orderResult.data.planName,
            order_id: orderResult.data.orderId,
            prefill: {
              name: userInfo.name || "User",
              email: userInfo.email || "user@example.com",
              contact: userInfo.phone || "",
            },
            theme: { color: "#ea580c" },
            handler: async function (response) {
              try {
                // Verify the renewal payment and extend subscription
                const verifyResult = await verifyRenewalPayment({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                });

                if (verifyResult.success) {
                  setIsProcessing(false);
                  resolve({
                    success: true,
                    data: {
                      ...response,
                      instanceId: instanceId,
                      subscription: verifyResult.data,
                    },
                  });
                } else {
                  throw new Error(
                    verifyResult.message ||
                      "Renewal payment verification failed"
                  );
                }
              } catch (error) {
                setIsProcessing(false);
                setError(error.message);
                reject(error);
              }
            },
            modal: {
              ondismiss: function () {
                setIsProcessing(false);
                reject(new Error("Payment cancelled by user"));
              },
            },
          };

          const razorpayInstance = new window.Razorpay(options);

          razorpayInstance.on("payment.failed", function (response) {
            setIsProcessing(false);
            const error = new Error(
              response.error.description || "Payment failed"
            );
            setError(error.message);
            reject(error);
          });

          razorpayInstance.open();
        });
      } catch (error) {
        setIsProcessing(false);
        setError(error.message);
        throw error;
      }
    },
    []
  );

  // Memoize clearError to prevent recreation on every render
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    processRenewalPayment,
    isProcessing,
    error,
    clearError,
  };
};

export default useRenewalPayment;
